import { useState } from 'react';
import { Shield, TriangleAlert, Construction, Camera, Gauge, Megaphone, X, Loader2, Check } from 'lucide-react';
import { AKCENT, CHYBA, POZOR, OK, tint } from '../ui';

/**
 * Dopravní info na obrazovce navigace (2026-10-23) — podle návrhu
 * „Dopravní info v navigaci rozvozce": značka limitu s tachometrem vlevo
 * dole, upozornění nahoře, oranžové tlačítko Nahlásit vpravo dole.
 * Nic se neodklikává — upozornění zmizí, když ho řidič projede.
 */

const fmtM = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1).replace('.', ',')} km` : `${Math.max(10, Math.round(m / 10) * 10)} m`);

/** Kruhová značka limitu (červený okraj) + skutečná rychlost. */
export function RychlostLimit({ limit, rychlostKmh, prekroceno, odvozeny, styl }) {
    if (limit == null && rychlostKmh == null) return null;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {limit != null && (
                <div
                    title={odvozeny ? 'Limit odvozený podle obce' : 'Limit podle mapy'}
                    style={{
                        width: 58, height: 58, borderRadius: '50%', background: '#fff', border: '7px solid #D7261E',
                        display: 'grid', placeItems: 'center', color: '#111', fontWeight: 900, fontSize: 23, lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums', boxShadow: '0 3px 12px rgba(0,0,0,0.35)', flexShrink: 0,
                    }}
                >
                    {limit}
                </div>
            )}
            {rychlostKmh != null && (
                <div style={{
                    ...styl, minWidth: 58, height: 52, borderRadius: 16, padding: '0 10px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                    ...(prekroceno ? { background: CHYBA, border: `1px solid ${CHYBA}`, color: '#fff' } : { color: 'var(--text-primary)' }),
                }}>
                    <span style={{ fontSize: 24, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>{rychlostKmh}</span>
                    <span style={{ fontSize: 10, fontWeight: 800, opacity: 0.75, marginTop: 2 }}>km/h</span>
                </div>
            )}
        </div>
    );
}

const VZHLED = {
    stopka:   { nazev: 'Stopka', barva: CHYBA, ikona: 'stop' },
    prednost: { nazev: 'Dej přednost', barva: CHYBA, ikona: 'prednost' },
    hlavni:   { nazev: 'Vjíždíš na hlavní', barva: POZOR, ikona: 'prednost' },
    radar:    { nazev: 'Radar', barva: POZOR, Ikona: Camera },
    usek:     { nazev: 'Úsekové měření', barva: POZOR, Ikona: Gauge },
    hlidka:   { nazev: 'Hlídka', barva: '#2F6FEB', Ikona: Shield },
    nehoda:   { nazev: 'Nehoda', barva: CHYBA, Ikona: TriangleAlert },
    prekazka: { nazev: 'Překážka', barva: POZOR, Ikona: Construction },
};

function IkonaUdalosti({ typ }) {
    const v = VZHLED[typ];
    if (v.ikona === 'stop') {
        return (
            <span style={{
                width: 34, height: 34, background: '#D7261E', color: '#fff', display: 'grid', placeItems: 'center',
                clipPath: 'polygon(30% 0,70% 0,100% 30%,100% 70%,70% 100%,30% 100%,0 70%,0 30%)',
                fontSize: 10, fontWeight: 900, letterSpacing: 0.3,
            }}>STOP</span>
        );
    }
    if (v.ikona === 'prednost') {
        return (
            <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
                <path d="M3 5 H31 L17 30 Z" fill="#fff" stroke="#D7261E" strokeWidth="4" strokeLinejoin="round" />
            </svg>
        );
    }
    const { Ikona } = v;
    return <Ikona size={28} color={v.barva} strokeWidth={2.4} />;
}

/** Pruh s nejbližší událostí na trase. */
export function Upozorneni({ udalost, styl }) {
    if (!udalost) return null;
    const v = VZHLED[udalost.typ];
    if (!v) return null;
    const stari = udalost.vytvoreno ? Math.max(1, Math.round((Date.now() - new Date(udalost.vytvoreno)) / 60000)) : null;
    return (
        <div role="status" style={{
            ...styl, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 18,
            border: `2px solid ${tint(v.barva, 0.75)}`, color: 'var(--text-primary)',
        }}>
            <div style={{ width: 36, height: 36, display: 'grid', placeItems: 'center', flexShrink: 0 }}><IkonaUdalosti typ={udalost.typ} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1.15 }}>{v.nazev}</div>
                {stari != null && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>
                        před {stari} min{udalost.kdo ? ` · ${udalost.kdo}` : ''}
                    </div>
                )}
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{fmtM(udalost.s)}</div>
        </div>
    );
}

/** Oranžové kulaté tlačítko — otevře nabídku hlášení. */
export function TlacitkoHlasit({ onClick, styl }) {
    return (
        <button type="button" onClick={onClick} aria-label="Nahlásit na silnici" style={{
            ...styl, width: 56, height: 56, borderRadius: '50%', display: 'grid', placeItems: 'center',
            background: AKCENT, border: 'none', color: '#17110A', cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,0.35)', WebkitTapHighlightColor: 'transparent',
        }}>
            <Megaphone size={26} strokeWidth={2.4} />
        </button>
    );
}

const VOLBY = [
    { typ: 'hlidka', nazev: 'Hlídka' },
    { typ: 'nehoda', nazev: 'Nehoda' },
    { typ: 'prekazka', nazev: 'Překážka' },
];

/**
 * Nabídka „Co je na silnici?". Za jízdy (nad 15 km/h) ťukat nejde —
 * řidič nesmí za volantem hledat tlačítka.
 */
export function NabidkaHlaseni({ rychlostKmh, onNahlasit, onZavrit }) {
    const [stav, setStav] = useState(null);   // null | typ (odesílá) | 'hotovo' | chyba
    const zaJizdy = rychlostKmh != null && rychlostKmh > 15;

    const odeslat = async (typ) => {
        setStav(typ);
        try {
            await onNahlasit(typ);
            setStav('hotovo');
            setTimeout(onZavrit, 900);
        } catch (e) {
            setStav(e.message || 'Nepodařilo se.');
        }
    };
    const chyba = stav && !['hotovo', 'hlidka', 'nehoda', 'prekazka'].includes(stav) ? stav : null;

    return (
        <div style={{ position: 'absolute', inset: 0, zIndex: 30, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-end' }}
            onClick={(e) => e.target === e.currentTarget && onZavrit()}>
            <div style={{
                width: '100%', background: 'var(--surface-raised)', borderRadius: '24px 24px 0 0',
                padding: '16px 14px calc(14px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', gap: 12,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, fontSize: 19, fontWeight: 900, color: 'var(--text-primary)' }}>Co je na silnici?</div>
                    <button type="button" onClick={onZavrit} aria-label="Zavřít" style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)', display: 'grid', placeItems: 'center' }}>
                        <X size={22} />
                    </button>
                </div>
                {zaJizdy && (
                    <div style={{ fontSize: 15, fontWeight: 700, color: POZOR }}>Zastav, pak nahlas — za jízdy to nejde.</div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                    {VOLBY.map((v) => {
                        const { Ikona, barva } = VZHLED[v.typ];
                        return (
                            <button key={v.typ} type="button" disabled={zaJizdy || !!stav} onClick={() => odeslat(v.typ)} style={{
                                height: 96, borderRadius: 18, border: `1px solid ${tint(barva, 0.4)}`, background: tint(barva, 0.12),
                                color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                                fontSize: 16, fontWeight: 900, opacity: zaJizdy ? 0.45 : 1, cursor: zaJizdy ? 'not-allowed' : 'pointer',
                            }}>
                                {stav === v.typ ? <Loader2 size={30} className="animate-spin" /> : stav === 'hotovo' ? <Check size={30} color={OK} /> : <Ikona size={30} color={barva} strokeWidth={2.4} />}
                                {v.nazev}
                            </button>
                        );
                    })}
                </div>
                {chyba && <div style={{ fontSize: 14, fontWeight: 700, color: CHYBA }}>{chyba}</div>}
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Hlášení uvidí ostatní řidiči na mapě a samo zmizí.</div>
            </div>
        </div>
    );
}

/** Element značky na mapě (MapaCanvas): hlášení řidičů a radary. */
export function prvekZnacky(typ) {
    const el = document.createElement('div');
    const barvy = { hlidka: '#2F6FEB', nehoda: '#EF4444', prekazka: '#F59E0B', radar: '#F59E0B' };
    const znak = { hlidka: '🛡', nehoda: '!', prekazka: '⚠', radar: '📷' };
    el.style.cssText = `
        width: 26px; height: 26px; border-radius: 50%; background: ${barvy[typ] ?? '#6B7280'};
        border: 2px solid #fff; box-shadow: 0 3px 10px rgba(0,0,0,0.45);
        display: flex; align-items: center; justify-content: center;
        color: #fff; font-weight: 900; font-size: 13px; line-height: 1; pointer-events: none;
    `;
    el.textContent = znak[typ] ?? '•';
    return el;
}
